import { create } from "zustand";
import { io, type Socket } from "socket.io-client";
import type { QueryKey } from "@tanstack/react-query";

type SocketWithFlags = Socket & {
  __noboriHandlersAttached?: boolean;
  __noboriUnloadAttached?: boolean;
};

type WindowWithSocket = Window & {
  __noboriSocket?: SocketWithFlags;
};

type VisibilitySubscriptionPayload = {
  brandId: string;
  days?: string;
  providers?: string;
  promptId?: string;
  includeActionables?: boolean;
  includeRunResponses?: boolean;
};

type CompetitorsSubscriptionPayload = {
  brandId: string;
  status?: string;
  providers?: string;
};

type PromptsSubscriptionPayload = {
  brandId: string;
  countries?: string[] | null;
  providers?: string;
  days?: number;
  startDate?: string;
  endDate?: string;
};

interface SocketState {
  socket: Socket | null;
  visibilitySubscriptions: Map<string, VisibilitySubscriptionPayload>;
  visibilityQueryKeys: Map<string, QueryKey>;
  visibilitySubscriptionCounts: Map<string, number>;
  competitorsSubscriptions: Map<string, CompetitorsSubscriptionPayload>;
  promptsSubscriptions: Map<string, PromptsSubscriptionPayload>;
  ensureSocket: (baseUrl: string) => Socket;
  addVisibilitySubscription: (
    signature: string,
    payload: VisibilitySubscriptionPayload,
  ) => void;
  removeVisibilitySubscription: (signature: string) => void;
  refreshVisibilitySubscription: (signature: string) => void;
  setVisibilityQueryKey: (signature: string, queryKey: QueryKey) => void;
  removeVisibilityQueryKey: (signature: string) => void;
  getVisibilityQueryKey: (signature: string) => QueryKey | undefined;
  clearVisibilitySubscriptionsForBrand: (brandId: string) => void;
  addCompetitorsSubscription: (
    signature: string,
    payload: CompetitorsSubscriptionPayload,
  ) => void;
  removeCompetitorsSubscription: (signature: string) => void;
  addPromptsSubscription: (
    signature: string,
    payload: PromptsSubscriptionPayload,
  ) => void;
  removePromptsSubscription: (signature: string) => void;
  clearPromptsSubscriptionsForBrand: (brandId: string) => void;
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  visibilitySubscriptions: new Map(),
  visibilityQueryKeys: new Map(),
  visibilitySubscriptionCounts: new Map(),
  competitorsSubscriptions: new Map(),
  promptsSubscriptions: new Map(),
  ensureSocket: (baseUrl) => {
    const existing = get().socket;
    if (existing) {
      return existing;
    }
    const win = window as WindowWithSocket;
    if (win.__noboriSocket) {
      set({ socket: win.__noboriSocket });
      return win.__noboriSocket;
    }
    const socket = io(baseUrl, {
      transports: ["websocket"],
      upgrade: true,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
    }) as SocketWithFlags;
    if (!socket.__noboriHandlersAttached) {
      socket.__noboriHandlersAttached = true;
      socket.on("connect", () => {
        const {
          visibilitySubscriptions,
          competitorsSubscriptions,
          promptsSubscriptions,
        } = get();
        visibilitySubscriptions.forEach((payload) => {
          socket.emit("visibility:subscribe", payload);
        });
        competitorsSubscriptions.forEach((payload) => {
          socket.emit("competitors:subscribe", payload);
        });
        promptsSubscriptions.forEach((payload) => {
          socket.emit("prompts:subscribe", payload);
        });
      });
      socket.on("disconnect", (reason) => {
        if (reason === "io server disconnect") {
          socket.connect();
        }
      });
    }
    if (!socket.__noboriUnloadAttached) {
      socket.__noboriUnloadAttached = true;
      window.addEventListener("beforeunload", () => {
        socket.disconnect();
      });
    }
    win.__noboriSocket = socket;
    set({ socket });
    return socket;
  },
  addVisibilitySubscription: (signature, payload) => {
    const { visibilitySubscriptions, visibilitySubscriptionCounts, socket } = get();
    if (visibilitySubscriptions.has(signature)) {
      const nextCounts = new Map(visibilitySubscriptionCounts);
      nextCounts.set(signature, (nextCounts.get(signature) ?? 0) + 1);
      set({ visibilitySubscriptionCounts: nextCounts });
      return;
    }
    const next = new Map(visibilitySubscriptions);
    next.set(signature, payload);
    const nextCounts = new Map(visibilitySubscriptionCounts);
    nextCounts.set(signature, 1);
    set({ visibilitySubscriptions: next, visibilitySubscriptionCounts: nextCounts });
    if (socket?.connected) {
      socket.emit("visibility:subscribe", payload);
    }
  },
  removeVisibilitySubscription: (signature) => {
    const { visibilitySubscriptions, visibilitySubscriptionCounts } = get();
    if (!visibilitySubscriptions.has(signature)) {
      return;
    }
    const nextCounts = new Map(visibilitySubscriptionCounts);
    const nextCount = (nextCounts.get(signature) ?? 1) - 1;
    if (nextCount <= 0) {
      const payload = visibilitySubscriptions.get(signature);
      nextCounts.delete(signature);
      const nextSubscriptions = new Map(visibilitySubscriptions);
      nextSubscriptions.delete(signature);
      set({ visibilitySubscriptions: nextSubscriptions, visibilitySubscriptionCounts: nextCounts });
      if (payload && get().socket?.connected) {
        get().socket?.emit("visibility:unsubscribe", payload);
      }
      return;
    }
    nextCounts.set(signature, nextCount);
    set({ visibilitySubscriptionCounts: nextCounts });
  },
  refreshVisibilitySubscription: (signature) => {
    const { visibilitySubscriptions, socket } = get();
    const payload = visibilitySubscriptions.get(signature);
    if (!payload || !socket?.connected) {
      return;
    }
    socket.emit("visibility:subscribe", payload);
  },
  setVisibilityQueryKey: (signature, queryKey) => {
    const { visibilityQueryKeys } = get();
    if (visibilityQueryKeys.get(signature) === queryKey) {
      return;
    }
    const next = new Map(visibilityQueryKeys);
    next.set(signature, queryKey);
    set({ visibilityQueryKeys: next });
  },
  removeVisibilityQueryKey: (signature) => {
    const { visibilityQueryKeys } = get();
    if (!visibilityQueryKeys.has(signature)) {
      return;
    }
    const next = new Map(visibilityQueryKeys);
    next.delete(signature);
    set({ visibilityQueryKeys: next });
  },
  getVisibilityQueryKey: (signature) => {
    return get().visibilityQueryKeys.get(signature);
  },
  clearVisibilitySubscriptionsForBrand: (brandId) => {
    if (!brandId) return;
    const { visibilitySubscriptions, visibilityQueryKeys, socket } = get();
    const nextSubscriptions = new Map(visibilitySubscriptions);
    const nextQueryKeys = new Map(visibilityQueryKeys);
    nextSubscriptions.forEach((payload, signature) => {
      if (payload.brandId === brandId) {
        nextSubscriptions.delete(signature);
        nextQueryKeys.delete(signature);
      }
    });
    set({
      visibilitySubscriptions: nextSubscriptions,
      visibilityQueryKeys: nextQueryKeys,
    });
    if (socket?.connected) {
      socket.emit("visibility:unsubscribe", { brandId });
    }
  },
  addCompetitorsSubscription: (signature, payload) => {
    const { competitorsSubscriptions, socket } = get();
    if (competitorsSubscriptions.has(signature)) {
      return;
    }
    const next = new Map(competitorsSubscriptions);
    next.set(signature, payload);
    set({ competitorsSubscriptions: next });
    if (socket?.connected) {
      socket.emit("competitors:subscribe", payload);
    }
  },
  removeCompetitorsSubscription: (signature) => {
    const { competitorsSubscriptions, socket } = get();
    if (!competitorsSubscriptions.has(signature)) {
      return;
    }
    const payload = competitorsSubscriptions.get(signature);
    const next = new Map(competitorsSubscriptions);
    next.delete(signature);
    set({ competitorsSubscriptions: next });
    if (payload && socket?.connected) {
      socket.emit("competitors:unsubscribe", payload);
    }
  },
  addPromptsSubscription: (signature, payload) => {
    const { promptsSubscriptions, socket } = get();
    if (promptsSubscriptions.has(signature)) {
      return;
    }
    const next = new Map(promptsSubscriptions);
    next.set(signature, payload);
    set({ promptsSubscriptions: next });
    if (socket?.connected) {
      socket.emit("prompts:subscribe", payload);
    }
  },
  removePromptsSubscription: (signature) => {
    const { promptsSubscriptions, socket } = get();
    if (!promptsSubscriptions.has(signature)) {
      return;
    }
    const payload = promptsSubscriptions.get(signature);
    const next = new Map(promptsSubscriptions);
    next.delete(signature);
    set({ promptsSubscriptions: next });
    if (payload && socket?.connected) {
      socket.emit("prompts:unsubscribe", payload);
    }
  },
  clearPromptsSubscriptionsForBrand: (brandId) => {
    if (!brandId) return;
    const { promptsSubscriptions, socket } = get();
    const nextSubscriptions = new Map(promptsSubscriptions);
    nextSubscriptions.forEach((payload, signature) => {
      if (payload.brandId === brandId) {
        nextSubscriptions.delete(signature);
      }
    });
    set({ promptsSubscriptions: nextSubscriptions });
    if (socket?.connected) {
      socket.emit("prompts:unsubscribe", { brandId });
    }
  },
}));
